import React, { useState, useRef, useEffect } from 'react';
import DynamicSidebar from '../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../components/ui/DashboardHeader';
import Icon from '../../../components/AppIcon';

// ─── Data ────────────────────────────────────────────────────────────────────

const PAYMENT_LINK_REQUEST = {
  title: "Invoice #2024-001",
  amount: "150.00",
  currency: "GHS",
  description: "Web design services payment",
  expiry_type: "7d",
  custom_expiry: null,
  payment_methods: { all_methods: false, card: true, mobile_money: true, crypto: false },
  billing_address: {
    first_name: "Kwame", last_name: "Mensah",
    email: "kwame.mensah@example.com", phone: "+233244000000",
    address_line1: "12 Independence Ave", city: "Accra",
    state: "Greater Accra", postal_code: "00233", country: "GH"
  },
  metadata: { order_id: "ORD-2024-001", customer_ref: "CUST-001" }
};

const PAYMENT_LINK_RESPONSE = {
  status: "success",
  message: "Payment link created successfully",
  data: {
    id: "PL-7F3K9X2M",
    link: "https://pay.example.com/link/PL-7F3K9X2M",
    title: "Invoice #2024-001",
    amount: "150.00", currency: "GHS",
    payment_methods: { card: true, mobile_money: true, crypto: false },
    expiry_type: "7d",
    expires_at: "2024-08-14T10:30:00Z",
    status: "active", paid: false,
    created_at: "2024-08-07T10:30:00Z"
  }
};

const PAYMENT_LINK_ERROR = {
  status: "error",
  message: "Validation failed",
  errors: [
    { field: "amount", message: "Amount must be a positive number" },
    { field: "currency", message: "Currency code is not supported" }
  ],
  code: "VALIDATION_ERROR"
};

const PAYMENT_LINK_FIELDS = [
  { field: "title", type: "string", required: true, description: "Display name for the payment link" },
  { field: "amount", type: "string", required: true, description: 'Payment amount as a decimal string (e.g. "150.00")' },
  { field: "currency", type: "string", required: true, description: "ISO 4217 currency code (e.g. GHS, USD, EUR)" },
  { field: "description", type: "string", required: false, description: "Optional description shown to the payer" },
  { field: "expiry_type", type: "enum", required: true, description: "One of: 1h, 24h, 7d, 30d, custom" },
  { field: "payment_methods.card", type: "boolean", required: false, description: "Enable card payments" },
  { field: "payment_methods.mobile_money", type: "boolean", required: false, description: "Enable mobile money payments" },
  { field: "billing_address.email", type: "string", required: false, description: "Payer\'s email address" },
  { field: "billing_address.country", type: "string", required: false, description: "ISO 3166-1 alpha-2 country code" },
  { field: "metadata", type: "object", required: false, description: "Arbitrary key-value pairs for your own reference" },
];

const DEPOSIT_REQUEST = {
  merchant_id: "test2",
  service: "KE_MPESA",
  callback_url: "https://webhook.site/f7bf8657-78ad-49e6-9f1d-7b64af498d58",
  transaction_id: "8a9ee4ad-9913-1904",
  currency: "KES",
  amount: 51,
  address: {
    first_name: "John", last_name: "Doe",
    email: "john.doe@gmail.com", phone_number: "254700178057",
    postal_code: "00200", country: "KE",
    administrative_area: "Nairobi", locality: "Westlands", company: "Doe LTD"
  }
};

const DEPOSIT_RESPONSE_200 = {
  status_code: 200,
  status_description: "Kindly check your device to authenticate the transaction",
  transaction_status: "PENDING",
  request_id: "EDC346069774",
  transaction_id: "8a9ee4ad-9913-1912",
  checkout_id: "ws_CO_16092026151726932702253116"
};

const DEPOSIT_CALLBACK_SUCCESS = {
  status_code: 200,
  status_description: "The service request is processed successfully.",
  transaction_status: "SUCCESS",
  request_id: "EDC346069774",
  transaction_id: "8a9ee4ad-9913-1912",
  checkout_id: "ws_CO_16092026151726932702253116"
};

const DEPOSIT_CALLBACK_FAILED = {
  status_code: 422,
  status_description: "Request Cancelled by user.",
  transaction_status: "FAILED",
  request_id: "732231AFA7C8",
  transaction_id: "8a9ee4ad-9913-1913",
  checkout_id: "ws_CO_16092026151928226702253116"
};

const DEPOSIT_ERROR_401 = { status_code: 401, status_description: "Decline - Expired token" };
const DEPOSIT_ERROR_400 = {
  status_code: 400, status_description: "Validation Failed",
  transaction_status: "FAILED",
  details: ["Phone number must be a 12-digit numeric value"]
};

const DEPOSIT_FIELDS = [
  { field: "merchant_id", type: "String", required: true, description: "Merchant identifier" },
  { field: "service", type: "String", required: true, description: "Mobile Money service. Example: KE_MPESA" },
  { field: "callback_url", type: "String", required: true, description: "Merchant endpoint that receives the final transaction status" },
  { field: "transaction_id", type: "String", required: true, description: "Unique transaction reference generated by the merchant" },
  { field: "currency", type: "String", required: true, description: "Transaction currency. Example: KES" },
  { field: "amount", type: "Number", required: true, description: "Transaction amount" },
  { field: "address.first_name", type: "String", required: true, description: "Customer first name" },
  { field: "address.last_name", type: "String", required: true, description: "Customer last name" },
  { field: "address.email", type: "String", required: true, description: "Customer email address" },
  { field: "address.phone_number", type: "String", required: true, description: "Customer mobile number" },
  { field: "address.postal_code", type: "String", required: true, description: "Customer postal code" },
  { field: "address.country", type: "String", required: true, description: "Two-character country code" },
  { field: "address.administrative_area", type: "String", required: true, description: "Administrative area/county" },
  { field: "address.locality", type: "String", required: true, description: "Customer locality" },
  { field: "address.company", type: "String", required: false, description: "Company name" },
];

const PAYOUT_REQUEST = {
  merchant_id: "test2",
  service: "KE_MPESA",
  callback_url: "https://webhook.site/46de3002-0d87-4340-b23c-b53ea91ab352",
  transaction_id: "W-7a67e4ad-02",
  currency: "KES",
  amount: 11,
  description: "Salary Payment",
  address: {
    first_name: "John", last_name: "Doe",
    email: "john.doe@gmail.com", phone_number: "254700178057",
    postal_code: "00200", country: "KE",
    administrative_area: "Nairobi", locality: "Thika", company: "Doe LTD"
  }
};

const PAYOUT_RESPONSE_200 = {
  status_code: 200,
  status_description: "Accept the service request successfully.",
  transaction_status: "PENDING",
  request_id: "9AE5DE69B454",
  transaction_id: "W-7a67e4ad-02",
  checkout_id: "AG_20260916_010010140wz0a6j4n4qs"
};

const PAYOUT_CALLBACK_SUCCESS = {
  status_code: 200,
  status_description: "The service request is processed successfully.",
  transaction_status: "SUCCESS",
  request_id: "9AE5DE69B454",
  transaction_id: "W-7a67e4ad-02",
  checkout_id: "AG_20260916_010010140wz0a6j4n4qs"
};

const PAYOUT_CALLBACK_FAILED = {
  status_code: 422,
  status_description: "The balance is insufficient for the transaction.",
  transaction_status: "FAILED",
  request_id: "9C6CD5251449",
  transaction_id: "W-7a67e4ad-03",
  checkout_id: "AG_20260916_010010140x1r35yj77r5"
};

const PAYOUT_ERROR_401 = { status_code: 401, status_description: "Decline - Expired token" };
const PAYOUT_ERROR_400 = {
  status_code: 400, status_description: "Validation Failed",
  transaction_status: "FAILED",
  details: ["Phone number must be a 12-digit numeric value"]
};

const PAYOUT_FIELDS = [
  { field: "merchant_id", type: "String", required: true, description: "Merchant identifier" },
  { field: "service", type: "String", required: true, description: "Mobile Money service (KE_MPESA, KE_AIRTEL, UG_MTN)" },
  { field: "callback_url", type: "String", required: true, description: "Merchant callback endpoint" },
  { field: "transaction_id", type: "String", required: true, description: "Unique merchant transaction reference" },
  { field: "currency", type: "String", required: true, description: "Transaction currency" },
  { field: "amount", type: "Number", required: true, description: "Amount to be paid out" },
  { field: "description", type: "String", required: false, description: "Description of the payout" },
  { field: "address", type: "Object", required: true, description: "Beneficiary information" },
];

const QUERY_REQUEST = { merchant_id: "test2", transaction_id: "EDC346069774" };

const QUERY_SUCCESS = {
  status_code: 200,
  status_description: "The service request is processed successfully.",
  transaction_status: "SUCCESS",
  request_id: "EDC346069774",
  transaction_id: "8a9ee4ad-9913-1912",
  checkout_id: "ws_CO_16092026151726932702253116"
};

const QUERY_FAILED = {
  status_code: 200,
  status_description: "Request Cancelled by user.",
  transaction_status: "FAILED",
  request_id: "732231AFA7C8",
  transaction_id: "8a9ee4ad-9913-1913",
  checkout_id: "ws_CO_16092026151928226702253116"
};

const QUERY_NOT_FOUND = {
  status_code: 400,
  status_description: "Transaction Number Not Found",
  transaction_status: "FAILED",
  transaction_id: "8a9ee4ad-9913-19131"
};

const QUERY_ERROR_401 = { status_code: 401, status_description: "Decline - Expired token" };

const SIG_EXAMPLE = {
  merchant_id: "test2",
  api_key: "m33dxDa3c2w6qmWIbKTmHl7MCgdVm98f",
  timestamp: "20260916165627",
  api_secret: "lsY1wLCPUXQ9",
  string_to_hash: "test2m33dxDa3c2w6qmWIbKTmHl7MCgdVm98f20260916165627lsY1wLCPUXQ9",
  sha256_result: "75e88ebc83e09aee2e5dd16ed506d086ce2bf65d37c4c2d4349a8aa1ce07f5c7"
};

const SIG_FIELDS = [
  { field: "merchant_id", description: "Merchant identifier" },
  { field: "api_key", description: "Merchant API key" },
  { field: "timestamp", description: "Current timestamp (yyyyMMddHHmmss, UTC+3)" },
  { field: "api_secret", description: "Merchant API secret" },
];

const JAVA_HASH_CODE = `public String generateHash(
        String merchantId,
        String consumerKey,
        String timestamp,
        String consumerSecret) {

    String payload = merchantId
            .concat(consumerKey)
            .concat(timestamp)
            .concat(consumerSecret);

    MessageDigest digest = MessageDigest.getInstance("SHA-256");

    byte[] hash = digest.digest(
            payload.getBytes(StandardCharsets.UTF_8)
    );

    StringBuilder hexString = new StringBuilder();

    for (byte b : hash) {
        String hex = Integer.toHexString(0xff & b);
        if (hex.length() == 1) hexString.append('0');
        hexString.append(hex);
    }

    return hexString.toString();
}`;

const JAVA_TIMESTAMP_CODE = `import java.time.ZoneId;
import java.time.ZonedDateTime;
import java.time.format.DateTimeFormatter;

public String generateTimestamp() {
    DateTimeFormatter formatter =
            DateTimeFormatter.ofPattern("yyyyMMddHHmmss");
    return ZonedDateTime.now(ZoneId.of("Africa/Nairobi"))
            .format(formatter);
}`;

const JAVA_FULL_EXAMPLE = `String merchantId = "test2";
String apiKey = "YOUR_API_KEY";
String apiSecret = "YOUR_API_SECRET";

String timestamp = generateTimestamp();

String signature = generateHash(
        merchantId,
        apiKey,
        timestamp,
        apiSecret
);

// Resulting request body:
// {
//   "merchant_id": "test2",
//   "timestamp": "20260916165627",
//   "signature": "75e88ebc83e09aee2e5dd16ed506d086ce2bf65d37c4c2d4349a8aa1ce07f5c7"
// }`;

// ─── Shared UI Primitives ─────────────────────────────────────────────────────

const CopyButton = ({ text, className = '' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard?.writeText(text)?.then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
        copied
          ? 'bg-green-500/20 text-green-400 border border-green-500/30' :'bg-white/10 text-gray-400 border border-white/10 hover:bg-white/20 hover:text-gray-200'
      } ${className}`}
    >
      <Icon name={copied ? 'Check' : 'Copy'} size={11} />
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const MethodBadge = ({ method }) => {
  const colors = {
    POST: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
    GET: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    PUT: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    DELETE: 'bg-red-500/20 text-red-300 border-red-500/30',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded border font-mono tracking-wider ${colors?.[method] || colors?.POST}`}>
      {method}
    </span>
  );
};

const HttpStatusBadge = ({ code }) => {
  const color =
    code >= 200 && code < 300 ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' :
    code >= 400 && code < 500 ? 'bg-red-500/15 text-red-400 border-red-500/25': 'bg-amber-500/15 text-amber-400 border-amber-500/25';
  const label =
    code === 200 ? 'OK' : code === 201 ? 'Created' : code === 400 ? 'Bad Request' :
    code === 401 ? 'Unauthorized' : code === 422 ? 'Unprocessable' : '';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-bold rounded border font-mono ${color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${code >= 200 && code < 300 ? 'bg-emerald-400' : code >= 400 && code < 500 ? 'bg-red-400' : 'bg-amber-400'}`} />
      {code} {label}
    </span>
  );
};

const EndpointBar = ({ method, path }) => (
  <div className="flex items-center gap-3 px-4 py-3 bg-[#0d1117] rounded-xl border border-white/8 font-mono">
    <MethodBadge method={method} />
    <span className="text-sm text-gray-300 flex-1 truncate">{path}</span>
    <CopyButton text={`${method} ${path}`} />
  </div>
);

const CodePanel = ({ data, isJson = true, lang = '' }) => {
  const text = isJson ? JSON.stringify(data, null, 2) : data;
  return (
    <div className="relative group bg-[#0d1117] rounded-xl border border-white/8 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8 bg-white/3">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        </div>
        {lang && <span className="text-xs text-gray-500 font-mono">{lang}</span>}
        <CopyButton text={text} />
      </div>
      <div className="p-4 overflow-x-auto max-h-72 overflow-y-auto">
        <pre className="text-xs leading-relaxed text-gray-300 font-mono whitespace-pre-wrap break-words">{text}</pre>
      </div>
    </div>
  );
};

const FieldTable = ({ fields }) => (
  <div className="overflow-x-auto rounded-xl border border-gray-200/60">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-200/60">
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Field</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Req.</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Description</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {fields?.map((row, i) => (
          <tr key={i} className="hover:bg-gray-50/70 transition-colors">
            <td className="px-4 py-3">
              <code className="text-xs font-mono text-primary bg-primary/8 px-1.5 py-0.5 rounded-md">{row?.field}</code>
            </td>
            <td className="px-4 py-3">
              <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{row?.type}</span>
            </td>
            <td className="px-4 py-3">
              {row?.required
                ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-500 text-xs font-semibold rounded-full border border-red-100">Yes</span>
                : <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-400 text-xs font-medium rounded-full">No</span>
              }
            </td>
            <td className="px-4 py-3 text-xs text-gray-500 leading-relaxed">{row?.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Tabs = ({ tabs, active, onChange }) => (
  <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
    {tabs?.map(tab => (
      <button
        key={tab?.id}
        onClick={() => onChange(tab?.id)}
        className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
          active === tab?.id
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        {tab?.label}
      </button>
    ))}
  </div>
);

// ─── Simulator ────────────────────────────────────────────────────────────────

const Simulator = ({ endpoint, requestData, successResponse, errorResponse, successLabel = '200 OK', errorLabel = '400 Error' }) => {
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [isError, setIsError] = useState(false);
  const [simulateError, setSimulateError] = useState(false);

  const run = async () => {
    setSimulating(true);
    setSimResult(null);
    await new Promise(r => setTimeout(r, 1200));
    setSimulating(false);
    setIsError(simulateError);
    setSimResult(simulateError ? errorResponse : successResponse);
  };

  return (
    <div className="mt-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/3 to-transparent overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-primary/10 bg-primary/5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
            <Icon name="Zap" size={15} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">API Simulator</p>
            <p className="text-xs text-gray-400 font-mono">{endpoint}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <button
              onClick={() => setSimulateError(!simulateError)}
              className={`relative w-9 h-5 rounded-full transition-colors ${simulateError ? 'bg-red-400' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${simulateError ? 'translate-x-4' : ''}`} />
            </button>
            <span className="text-xs text-gray-500">Simulate error</span>
          </label>
          <button
            onClick={run}
            disabled={simulating}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-primary/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
          >
            {simulating
              ? <><Icon name="Loader2" size={13} className="animate-spin" />Sending…</>
              : <><Icon name="Play" size={13} />Run</>
            }
          </button>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Request Payload</p>
          <CodePanel data={requestData} isJson={true} lang="json" />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Simulated Response</p>
          {simulating ? (
            <div className="bg-[#0d1117] rounded-xl border border-white/8 h-[calc(100%-28px)] flex items-center justify-center min-h-40">
              <div className="text-center">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-xs text-gray-500">Sending request…</p>
              </div>
            </div>
          ) : simResult ? (
            <div className="bg-[#0d1117] rounded-xl border border-white/8 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8 bg-white/3">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-bold rounded border font-mono ${isError ? 'bg-red-500/15 text-red-400 border-red-500/25' : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isError ? 'bg-red-400' : 'bg-emerald-400'}`} />
                  {isError ? errorLabel : successLabel}
                </span>
                <CopyButton text={JSON.stringify(simResult, null, 2)} />
              </div>
              <div className="p-4 overflow-x-auto max-h-72 overflow-y-auto">
                <pre className="text-xs leading-relaxed text-gray-300 font-mono whitespace-pre-wrap break-words">{JSON.stringify(simResult, null, 2)}</pre>
              </div>
            </div>
          ) : (
            <div className="bg-[#0d1117] rounded-xl border border-white/8 min-h-40 flex items-center justify-center">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-2">
                  <Icon name="Play" size={18} className="text-gray-600" />
                </div>
                <p className="text-xs text-gray-600">Click Run to see the response</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-5 pb-5">
        <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-100 rounded-xl">
          <Icon name="Info" size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            All API requests require a valid <strong>Bearer token</strong> in the{' '}
            <code className="font-mono bg-amber-100 px-1 rounded">Authorization</code> header.
            Obtain credentials from <strong>Integration → API Keys</strong>.
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Section Wrapper ──────────────────────────────────────────────────────────

const SectionCard = ({ id, number, title, description, isOpen, onToggle, children, accentColor = 'primary' }) => {
  const accentMap = {
    primary: { dot: 'bg-primary', num: 'text-primary bg-primary/10 border-primary/20', ring: 'ring-primary/20', chevron: 'text-primary' },
    blue: { dot: 'bg-blue-500', num: 'text-blue-600 bg-blue-50 border-blue-200', ring: 'ring-blue-200', chevron: 'text-blue-500' },
    violet: { dot: 'bg-violet-500', num: 'text-violet-600 bg-violet-50 border-violet-200', ring: 'ring-violet-200', chevron: 'text-violet-500' },
    emerald: { dot: 'bg-emerald-500', num: 'text-emerald-600 bg-emerald-50 border-emerald-200', ring: 'ring-emerald-200', chevron: 'text-emerald-500' },
  };
  const a = accentMap?.[accentColor] || accentMap?.primary;

  return (
    <section id={id} className="mb-6">
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all text-left group shadow-sm hover:shadow-md ${isOpen ? `ring-2 ${a?.ring}` : ''}`}
      >
        <div className={`w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0 font-bold text-sm ${a?.num}`}>
          {number}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-900 group-hover:text-gray-700">{title}</p>
          <p className="text-xs text-gray-400 mt-0.5 truncate">{description}</p>
        </div>
        <div className={`flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <Icon name="ChevronDown" size={16} className="text-gray-400" />
        </div>
      </button>

      {isOpen && (
        <div className="mt-3 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-6">
            {children}
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Section: Create Payment Link ─────────────────────────────────────────────

const CreatePaymentLinkSection = ({ isOpen, onToggle }) => {
  const [tab, setTab] = useState('request');
  const tabs = [
    { id: 'request', label: 'Request' },
    { id: 'response', label: 'Responses' },
    { id: 'fields', label: 'Fields' },
  ];

  return (
    <SectionCard
      id="section-1"
      number="1"
      title="Create Payment Link"
      description="POST /api/v1/payment-links — Generate a shareable payment link"
      isOpen={isOpen}
      onToggle={onToggle}
      accentColor="primary"
    >
      <EndpointBar method="POST" path="/api/v1/payment-links" />

      <div className="mt-5">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      <div className="mt-4">
        {tab === 'request' && (
          <div>
            <p className="text-xs text-gray-400 mb-3 font-mono">Content-Type: application/json</p>
            <CodePanel data={PAYMENT_LINK_REQUEST} isJson lang="json" />
          </div>
        )}

        {tab === 'response' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={201} /><span className="text-xs font-semibold text-gray-600">Success Response</span></div>
              <CodePanel data={PAYMENT_LINK_RESPONSE} isJson lang="json" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={422} /><span className="text-xs font-semibold text-gray-600">Validation Error</span></div>
              <CodePanel data={PAYMENT_LINK_ERROR} isJson lang="json" />
            </div>
          </div>
        )}

        {tab === 'fields' && <FieldTable fields={PAYMENT_LINK_FIELDS} />}
      </div>

      <Simulator
        endpoint="POST /api/v1/payment-links"
        requestData={PAYMENT_LINK_REQUEST}
        successResponse={PAYMENT_LINK_RESPONSE}
        errorResponse={PAYMENT_LINK_ERROR}
        successLabel="201 Created"
        errorLabel="422 Unprocessable"
      />
    </SectionCard>
  );
};

// ─── Section: Mobile Transactions ─────────────────────────────────────────────

const MobileTransactionsSection = ({ isOpen, onToggle }) => {
  const [subTab, setSubTab] = useState('deposit');
  const [depositTab, setDepositTab] = useState('request');
  const [payoutTab, setPayoutTab] = useState('request');

  const depositTabs = [
    { id: 'request', label: 'Request' },
    { id: 'response', label: 'Responses' },
    { id: 'callback', label: 'Callbacks' },
    { id: 'fields', label: 'Fields' },
  ];
  const payoutTabs = [
    { id: 'request', label: 'Request' },
    { id: 'response', label: 'Responses' },
    { id: 'callback', label: 'Callbacks' },
    { id: 'fields', label: 'Fields' },
  ];

  return (
    <SectionCard
      id="section-2"
      number="2"
      title="Mobile Transactions"
      description="STK Push deposits & B2C payouts via mobile money networks"
      isOpen={isOpen}
      onToggle={onToggle}
      accentColor="blue"
    >
      {/* Sub-nav */}
      <div className="flex gap-2 mb-5">
        {[
          { id: 'deposit', label: 'a) Deposit — STK Push', endpoint: 'POST :/mobile/deposit' },
          { id: 'payout', label: 'b) Payout — B2C', endpoint: 'POST :/mobile/payout' },
        ]?.map(s => (
          <button
            key={s?.id}
            onClick={() => setSubTab(s?.id)}
            className={`flex-1 flex flex-col items-start px-4 py-3 rounded-xl border text-left transition-all ${
              subTab === s?.id
                ? 'bg-blue-50 border-blue-200 text-blue-700' :'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
            }`}
          >
            <span className="text-xs font-bold">{s?.label}</span>
            <span className="text-xs font-mono opacity-70 mt-0.5">{s?.endpoint}</span>
          </button>
        ))}
      </div>

      {/* Deposit */}
      {subTab === 'deposit' && (
        <div>
          <EndpointBar method="POST" path=":/mobile/deposit" />
          <div className="mt-5">
            <Tabs tabs={depositTabs} active={depositTab} onChange={setDepositTab} />
          </div>
          <div className="mt-4">
            {depositTab === 'request' && (
              <div>
                <p className="text-xs text-gray-400 mb-3 font-mono">Authorization: Bearer &lt;token&gt; · Content-Type: application/json</p>
                <CodePanel data={DEPOSIT_REQUEST} isJson lang="json" />
              </div>
            )}
            {depositTab === 'response' && (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={200} /><span className="text-xs font-semibold text-gray-600">Acknowledgement — PENDING</span></div>
                  <CodePanel data={DEPOSIT_RESPONSE_200} isJson lang="json" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={401} /><span className="text-xs font-semibold text-gray-600">Expired Token</span></div>
                  <CodePanel data={DEPOSIT_ERROR_401} isJson lang="json" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={400} /><span className="text-xs font-semibold text-gray-600">Validation Error</span></div>
                  <CodePanel data={DEPOSIT_ERROR_400} isJson lang="json" />
                </div>
              </div>
            )}
            {depositTab === 'callback' && (
              <div className="space-y-4">
                <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <Icon name="Info" size={13} className="text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    The <code className="font-mono bg-blue-100 px-1 rounded">callback_url</code> receives the final status via POST.
                    Use <code className="font-mono bg-blue-100 px-1 rounded">transaction_status</code> as the primary indicator: <strong>PENDING</strong>, <strong>SUCCESS</strong>, <strong>FAILED</strong>.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-bold rounded-full border border-emerald-200">SUCCESS</span>
                    <span className="text-xs font-semibold text-gray-600">Successful Callback</span>
                  </div>
                  <CodePanel data={DEPOSIT_CALLBACK_SUCCESS} isJson lang="json" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs font-bold rounded-full border border-red-200">FAILED</span>
                    <span className="text-xs font-semibold text-gray-600">Failed Callback</span>
                  </div>
                  <CodePanel data={DEPOSIT_CALLBACK_FAILED} isJson lang="json" />
                </div>
              </div>
            )}
            {depositTab === 'fields' && <FieldTable fields={DEPOSIT_FIELDS} />}
          </div>
          <Simulator
            endpoint="POST :/mobile/deposit"
            requestData={DEPOSIT_REQUEST}
            successResponse={DEPOSIT_RESPONSE_200}
            errorResponse={DEPOSIT_ERROR_400}
            successLabel="200 OK — PENDING"
            errorLabel="400 Validation Error"
          />
        </div>
      )}

      {/* Payout */}
      {subTab === 'payout' && (
        <div>
          {/* Supported services */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { code: 'KE_MPESA', label: 'Kenya M-PESA' },
              { code: 'KE_AIRTEL', label: 'Kenya Airtel Money' },
              { code: 'UG_MTN', label: 'Uganda MTN Mobile Money' },
            ]?.map(s => (
              <span key={s?.code} className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600 border border-gray-200">
                <code className="font-mono text-primary">{s?.code}</code>
                <span className="text-gray-400">·</span>
                {s?.label}
              </span>
            ))}
          </div>
          <EndpointBar method="POST" path=":/mobile/payout" />
          <div className="mt-5">
            <Tabs tabs={payoutTabs} active={payoutTab} onChange={setPayoutTab} />
          </div>
          <div className="mt-4">
            {payoutTab === 'request' && (
              <div>
                <p className="text-xs text-gray-400 mb-3 font-mono">Authorization: Bearer &lt;token&gt; · Content-Type: application/json</p>
                <CodePanel data={PAYOUT_REQUEST} isJson lang="json" />
              </div>
            )}
            {payoutTab === 'response' && (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={200} /><span className="text-xs font-semibold text-gray-600">Acknowledgement — PENDING</span></div>
                  <CodePanel data={PAYOUT_RESPONSE_200} isJson lang="json" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={401} /><span className="text-xs font-semibold text-gray-600">Expired Token</span></div>
                  <CodePanel data={PAYOUT_ERROR_401} isJson lang="json" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={400} /><span className="text-xs font-semibold text-gray-600">Validation Error</span></div>
                  <CodePanel data={PAYOUT_ERROR_400} isJson lang="json" />
                </div>
              </div>
            )}
            {payoutTab === 'callback' && (
              <div className="space-y-4">
                <div className="flex items-start gap-2.5 p-3 bg-violet-50 border border-violet-100 rounded-xl">
                  <Icon name="Info" size={13} className="text-violet-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-violet-700">
                    Store <code className="font-mono bg-violet-100 px-1 rounded">request_id</code>, <code className="font-mono bg-violet-100 px-1 rounded">transaction_id</code>, and <code className="font-mono bg-violet-100 px-1 rounded">checkout_id</code> for reconciliation.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-bold rounded-full border border-emerald-200">SUCCESS</span>
                    <span className="text-xs font-semibold text-gray-600">Successful Callback</span>
                  </div>
                  <CodePanel data={PAYOUT_CALLBACK_SUCCESS} isJson lang="json" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs font-bold rounded-full border border-red-200">FAILED</span>
                    <span className="text-xs font-semibold text-gray-600">Failed Callback</span>
                  </div>
                  <CodePanel data={PAYOUT_CALLBACK_FAILED} isJson lang="json" />
                </div>
              </div>
            )}
            {payoutTab === 'fields' && <FieldTable fields={PAYOUT_FIELDS} />}
          </div>
          <Simulator
            endpoint="POST :/mobile/payout"
            requestData={PAYOUT_REQUEST}
            successResponse={PAYOUT_RESPONSE_200}
            errorResponse={PAYOUT_ERROR_400}
            successLabel="200 OK — PENDING"
            errorLabel="400 Validation Error"
          />
        </div>
      )}
    </SectionCard>
  );
};

// ─── Section: Query Transactions ──────────────────────────────────────────────

const QueryTransactionsSection = ({ isOpen, onToggle }) => {
  const [tab, setTab] = useState('request');
  const tabs = [
    { id: 'request', label: 'Request' },
    { id: 'response', label: 'Responses' },
  ];

  return (
    <SectionCard
      id="section-3"
      number="3"
      title="Query Transactions"
      description="POST :/mobile/transaction/status — Retrieve transaction status"
      isOpen={isOpen}
      onToggle={onToggle}
      accentColor="emerald"
    >
      <EndpointBar method="POST" path=":/mobile/transaction/status" />

      <div className="mt-5">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      <div className="mt-4">
        {tab === 'request' && (
          <div>
            <p className="text-xs text-gray-400 mb-3 font-mono">Authorization: Bearer &lt;token&gt; · Content-Type: application/json</p>
            <CodePanel data={QUERY_REQUEST} isJson lang="json" />
            <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200/60">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Field</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Req.</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { field: 'merchant_id', type: 'String', description: 'Merchant identifier' },
                    { field: 'transaction_id', type: 'String', description: 'The transaction ID or request ID to query' },
                  ]?.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50/70 transition-colors">
                      <td className="px-4 py-3"><code className="text-xs font-mono text-primary bg-primary/8 px-1.5 py-0.5 rounded-md">{row?.field}</code></td>
                      <td className="px-4 py-3"><span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{row?.type}</span></td>
                      <td className="px-4 py-3"><span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-500 text-xs font-semibold rounded-full border border-red-100">Yes</span></td>
                      <td className="px-4 py-3 text-xs text-gray-500">{row?.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'response' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={200} /><span className="text-xs font-semibold text-gray-600">Successful Transaction</span></div>
              <CodePanel data={QUERY_SUCCESS} isJson lang="json" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={200} /><span className="text-xs font-semibold text-gray-600">Failed Transaction</span></div>
              <CodePanel data={QUERY_FAILED} isJson lang="json" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={400} /><span className="text-xs font-semibold text-gray-600">Transaction Not Found</span></div>
              <CodePanel data={QUERY_NOT_FOUND} isJson lang="json" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2"><HttpStatusBadge code={401} /><span className="text-xs font-semibold text-gray-600">Expired Bearer Token</span></div>
              <CodePanel data={QUERY_ERROR_401} isJson lang="json" />
            </div>
          </div>
        )}
      </div>

      <Simulator
        endpoint="POST :/mobile/transaction/status"
        requestData={QUERY_REQUEST}
        successResponse={QUERY_SUCCESS}
        errorResponse={QUERY_NOT_FOUND}
        successLabel="200 OK — SUCCESS"
        errorLabel="400 Not Found"
      />
    </SectionCard>
  );
};

// ─── Section: Signature Generation ───────────────────────────────────────────

const SignatureGenerationSection = ({ isOpen, onToggle }) => {
  const [tab, setTab] = useState('overview');
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'java', label: 'Java' },
    { id: 'example', label: 'Full Example' },
  ];

  return (
    <SectionCard
      id="section-4"
      number="4"
      title="Signature Generation"
      description="SHA-256 signature required for authentication token requests"
      isOpen={isOpen}
      onToggle={onToggle}
      accentColor="violet"
    >
      <div className="mb-5">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Formula</p>
            <div className="bg-[#0d1117] rounded-xl border border-white/8 px-5 py-4">
              <code className="text-sm text-emerald-400 font-mono">SHA256(merchant_id + api_key + timestamp + api_secret)</code>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Parameters</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200/60">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Parameter</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {SIG_FIELDS?.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50/70 transition-colors">
                      <td className="px-4 py-3"><code className="text-xs font-mono text-primary bg-primary/8 px-1.5 py-0.5 rounded-md">{row?.field}</code></td>
                      <td className="px-4 py-3 text-xs text-gray-500">{row?.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Timestamp Format</p>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-primary bg-primary/8 px-2 py-1 rounded-lg">yyyyMMddHHmmss</code>
                <span className="text-xs text-gray-400">— UTC+3 (Africa/Nairobi)</span>
              </div>
              <p className="text-xs text-gray-500">
                Example: <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">20260916165627</code>
                <span className="ml-2 text-gray-400">→ 16 Sep 2026 16:56:27 UTC+3</span>
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Worked Example</p>
            <div className="space-y-3">
              <div className="bg-[#0d1117] rounded-xl border border-white/8 p-4 font-mono text-xs space-y-1.5">
                {[
                  ['merchant_id', SIG_EXAMPLE?.merchant_id],
                  ['api_key    ', SIG_EXAMPLE?.api_key],
                  ['timestamp  ', SIG_EXAMPLE?.timestamp],
                  ['api_secret ', SIG_EXAMPLE?.api_secret],
                ]?.map(([k, v]) => (
                  <p key={k}><span className="text-gray-500">{k} = </span><span className="text-gray-200">{v}</span></p>
                ))}
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1.5">String to hash:</p>
                <div className="bg-[#0d1117] rounded-xl border border-white/8 px-4 py-3">
                  <code className="text-xs text-yellow-300 font-mono break-all">{SIG_EXAMPLE?.string_to_hash}</code>
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1.5">SHA-256 result:</p>
                <div className="bg-[#0d1117] rounded-xl border border-white/8 px-4 py-3 flex items-center justify-between gap-3">
                  <code className="text-xs text-emerald-400 font-mono break-all">{SIG_EXAMPLE?.sha256_result}</code>
                  <CopyButton text={SIG_EXAMPLE?.sha256_result} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'java' && (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Hash Generation</p>
            <CodePanel data={JAVA_HASH_CODE} isJson={false} lang="java" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Timestamp Generation</p>
            <CodePanel data={JAVA_TIMESTAMP_CODE} isJson={false} lang="java" />
          </div>
        </div>
      )}

      {tab === 'example' && (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Complete Token Generation</p>
            <CodePanel data={JAVA_FULL_EXAMPLE} isJson={false} lang="java" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Resulting Auth Request Body</p>
            <CodePanel data={{ merchant_id: "test2", timestamp: "20260916165627", signature: SIG_EXAMPLE?.sha256_result }} isJson lang="json" />
          </div>
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ShieldAlert" size={14} className="text-amber-600" />
              <p className="text-xs font-bold text-amber-800">Security Recommendations</p>
            </div>
            <ul className="text-xs text-amber-700 space-y-1.5 list-disc list-inside">
              <li>Keep <code className="font-mono bg-amber-100 px-1 rounded">api_key</code> and <code className="font-mono bg-amber-100 px-1 rounded">api_secret</code> confidential at all times</li>
              <li>Never expose the API secret in frontend or browser applications</li>
              <li>Generate a fresh timestamp and signature for each token request</li>
              <li>Store the returned access token securely and use it until expiry</li>
            </ul>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

// ─── Status Codes Reference ───────────────────────────────────────────────────

const StatusCodesRef = () => {
  const codes = [
    { code: 200, label: 'OK', desc: 'Request processed successfully', color: 'emerald' },
    { code: 400, label: 'Bad Request', desc: 'Invalid request or validation failure', color: 'red' },
    { code: 401, label: 'Unauthorized', desc: 'Invalid or expired token', color: 'red' },
    { code: 422, label: 'Unprocessable', desc: 'Transaction could not be completed', color: 'amber' },
  ];
  const colorMap = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      {codes?.map(c => (
        <div key={c?.code} className={`flex items-start gap-3 p-3 rounded-xl border ${colorMap?.[c?.color]}`}>
          <span className="font-mono font-bold text-sm">{c?.code}</span>
          <div>
            <p className="text-xs font-semibold">{c?.label}</p>
            <p className="text-xs opacity-75 mt-0.5">{c?.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ApiDocumentationPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (section) => {
    setActiveSection(prev => prev === section ? null : section);
  };

  const navItems = [
    { id: 'section-1', label: 'Create Payment Link', method: 'POST', sub: '/payment-links' },
    { id: 'section-2', label: 'Mobile Transactions', method: 'POST', sub: '/mobile/deposit · /payout' },
    { id: 'section-3', label: 'Query Transactions', method: 'POST', sub: '/transaction/status' },
    { id: 'section-4', label: 'Signature Generation', method: null, sub: 'SHA-256 auth' },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id?.replace('section-', 'section-'));
    toggleSection(id?.replace('section-', 'section-'));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        isSidebarCollapsed={sidebarCollapsed}
      />
      <DynamicSidebar
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`transition-all duration-300 pt-16 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
              <span>Integration</span>
              <Icon name="ChevronRight" size={12} />
              <span className="text-gray-600 font-medium">API Documentation</span>
            </div>

            <div className="bg-gradient-to-r from-[#1a0a4e] to-[#3d2a8a] rounded-2xl p-6 text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, #7c6fe0 0%, transparent 60%)' }} />
              <div className="relative flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                      <Icon name="BookOpen" size={16} className="text-white" />
                    </div>
                    <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">Merchant API</span>
                  </div>
                  <h1 className="text-2xl font-bold text-white">API Documentation & Simulations</h1>
                  <p className="text-sm text-white/60 mt-1 max-w-lg">
                    Explore request/response schemas, field references, and run live simulations against the Merchant API.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 text-white rounded-full text-xs font-semibold border border-white/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 inline-block" />v1
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-semibold border border-emerald-500/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />REST
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 text-white rounded-full text-xs font-semibold border border-white/20">
                    <Icon name="Lock" size={11} />Bearer Auth
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="flex gap-6 items-start">

            {/* Left sticky nav */}
            <div className="hidden xl:block w-64 flex-shrink-0">
              <div className="sticky top-24 space-y-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-3">Sections</p>
                {navItems?.map((item, i) => (
                  <button
                    key={item?.id}
                    onClick={() => scrollTo(item?.id)}
                    className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition-all group ${
                      activeSection === item?.id
                        ? 'bg-primary/8 text-primary' :'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                      activeSection === item?.id ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
                    }`}>{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold truncate">{item?.label}</p>
                      <p className="text-xs opacity-60 font-mono truncate mt-0.5">{item?.sub}</p>
                    </div>
                  </button>
                ))}

                <div className="mt-6 pt-5 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-3">Status Codes</p>
                  <div className="px-3 space-y-1.5">
                    {[
                      { code: '200', label: 'OK', color: 'text-emerald-600' },
                      { code: '400', label: 'Bad Request', color: 'text-red-500' },
                      { code: '401', label: 'Unauthorized', color: 'text-red-500' },
                      { code: '422', label: 'Unprocessable', color: 'text-amber-600' },
                    ]?.map(s => (
                      <div key={s?.code} className="flex items-center gap-2">
                        <code className={`text-xs font-mono font-bold ${s?.color}`}>{s?.code}</code>
                        <span className="text-xs text-gray-400">{s?.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <CreatePaymentLinkSection isOpen={activeSection === 'section-1'} onToggle={() => toggleSection('section-1')} />
              <MobileTransactionsSection isOpen={activeSection === 'section-2'} onToggle={() => toggleSection('section-2')} />
              <QueryTransactionsSection isOpen={activeSection === 'section-3'} onToggle={() => toggleSection('section-3')} />
              <SignatureGenerationSection isOpen={activeSection === 'section-4'} onToggle={() => toggleSection('section-4')} />

              {/* Status codes card */}
              <div className="mt-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Hash" size={15} className="text-gray-400" />
                  <p className="text-sm font-bold text-gray-700">HTTP Status Code Reference</p>
                </div>
                <StatusCodesRef />
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default ApiDocumentationPage;
